import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddCompanionCard from './AddCompanionCard';
import { fetchCompanions } from '../../api/companions';
import CompanionCard from './CompanionCard';
import CompanionsEmptyState from './CompanionsEmptyState';
import ReportIssueButton from '../support/ReportIssueButton';
import { supportContextFromApiError } from '../../utils/supportReports';

const CompanionsSection = ({
  limit = 4,
  mode = 'preview',
  onEmptyChange,
  onChat,
  onInvite,
  onOpenProfile,
  onRemove,
  onViewAll,
}) => {
  const { t } = useTranslation();
  const [companions, setCompanions] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState('');
  const [errorContext, setErrorContext] = useState(null);
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const showPagination = totalPages > 1;

  useEffect(() => {
    let mounted = true;

    setLoading(true);
    const loadCompanions = () =>
      fetchCompanions({ limit, page })
      .then((data) => {
        if (!mounted) return;
        setCompanions(data.items || []);
        setTotal(data.total || 0);
        setHasMore(Boolean(data.hasMore));
        onEmptyChange?.((data.total || 0) === 0);
        setErrorKey('');
        setErrorContext(null);
      })
      .catch((error) => {
        if (!mounted) return;
        setErrorKey('companions.feedback.loadError');
        setErrorContext(supportContextFromApiError(error, { pageName: 'Companions' }));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    loadCompanions();
    const intervalId = window.setInterval(loadCompanions, 45 * 1000);
    const handleServerEvent = (event) => {
      if (event.detail?.type !== 'presence.updated') return;

      setCompanions((current) =>
        current.map((companion) =>
          companion.id === event.detail.userId
            ? { ...companion, presence: event.detail.presence }
            : companion
        )
      );
    };

    window.addEventListener('thetavern:server-event', handleServerEvent);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener('thetavern:server-event', handleServerEvent);
    };
  }, [limit, onEmptyChange, page]);

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={84} />
            <Skeleton variant="rounded" height={84} />
            {mode === 'list' ? <Skeleton variant="rounded" height={84} /> : null}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (errorKey) {
    return (
      <Alert
        severity="error"
        action={<ReportIssueButton context={errorContext} sx={{ whiteSpace: 'nowrap' }} />}
      >
        {t(errorKey)}
      </Alert>
    );
  }

  if (companions.length === 0) {
    return <CompanionsEmptyState onFind={onViewAll} onInvite={onInvite} />;
  }

  if (mode === 'preview') {
    return (
      <Card variant="outlined">
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(4, minmax(0, 1fr))',
                md: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(4, minmax(0, 1fr))',
              },
              columnGap: { xs: 1.5, sm: 2 },
              rowGap: 2.5,
              justifyItems: 'center',
              alignItems: 'start',
              '& > :last-child:nth-of-type(odd)': {
                gridColumn: {
                  xs: companions.length > 1 ? '1 / -1' : 'auto',
                  sm: 'auto',
                  md: companions.length > 1 ? '1 / -1' : 'auto',
                  xl: 'auto',
                },
              },
            }}
          >
            {onInvite ? <AddCompanionCard compact onClick={() => onInvite()} /> : null}
            {companions.map((companion) => (
              <CompanionCard
                key={companion.id}
                companion={companion}
                variant="compact"
                onInvite={onInvite}
                onChat={onChat}
                onOpenProfile={onOpenProfile}
                onRemove={onRemove}
                onView={onViewAll}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Typography color="text.secondary">
        {t('companions.list.friendsAtTable', { count: total })}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 2.5,
        }}
      >
        {onInvite ? <AddCompanionCard compact onClick={() => onInvite()} /> : null}
        {companions.map((companion) => (
          <CompanionCard
            key={companion.id}
            companion={companion}
            onInvite={onInvite}
            onChat={onChat}
            onOpenProfile={onOpenProfile}
            onRemove={onRemove}
            onView={onViewAll}
          />
        ))}
      </Box>
      {showPagination ? (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button disabled={page === 1} onClick={() => setPage((current) => current - 1)}>
            {t('common.previous')}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {t('common.page', { page, total: totalPages })}
          </Typography>
          <Button disabled={!hasMore} onClick={() => setPage((current) => current + 1)}>
            {t('common.next')}
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
};

export default CompanionsSection;
